import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, ShieldCheck, ArrowRight, Phone, Mail } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ContactForm from "@/components/ContactForm";
import ProductImage from "@/components/ProductImage";
import { Button } from "@/components/ui/button";
import { fetchProduct, fetchProducts } from "@/lib/api";
import { SITE } from "@/lib/site";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    setProduct(null);
    setError(false);
    fetchProduct(slug)
      .then((p) => {
        setProduct(p);
        fetchProducts({ category: p.category_slug }).then((list) => {
          setRelated(list.filter((x) => x.slug !== p.slug).slice(0, 3));
        });
      })
      .catch(() => setError(true));
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-white text-[#0A1128]">
        <Header />
        <div className="max-w-4xl mx-auto px-6 lg:px-12 pt-40 pb-24">
          <div data-testid="product-not-found" className="border border-slate-200 bg-slate-50 p-12 text-center">
            <div className="overline">// 404</div>
            <h1 className="font-display font-black uppercase text-3xl mt-3">Equipment not found</h1>
            <Link to="/products" className="mt-6 inline-flex items-center gap-2 text-blue-600 hover:text-blue-700">
              <ArrowLeft className="w-4 h-4" /> Back to catalog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white text-[#0A1128]">
        <Header />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-40 pb-24">
          <div className="overline">// loading…</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="product-detail-page" className="min-h-screen bg-white text-[#0A1128]">
      <Header />
      <main className="pt-28">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-8">
          <div className="flex items-center flex-wrap gap-2 overline">
            <Link to="/" className="hover:text-[#0A1128]">Home</Link>
            <span>/</span>
            <Link to="/products" className="hover:text-[#0A1128]">Equipment</Link>
            <span>/</span>
            <Link to={`/products?category=${product.category_slug}`} className="hover:text-[#0A1128]">{product.category_name}</Link>
            <span>/</span>
            <span className="text-[#0A1128]">{product.short_name}</span>
          </div>
        </div>

        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Image */}
          <div className="lg:col-span-6">
            <div className="relative border border-slate-200 bg-slate-50 aspect-square flex items-center justify-center overflow-hidden corner-tl corner-tr corner-bl corner-br shadow-hard">
              <ProductImage product={product} className="p-12" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-red-600 text-white px-3 py-1 text-[10px] uppercase tracking-widest font-bold">
                <ShieldCheck className="w-3.5 h-3.5" /> {product.category_name}
              </div>
              <div className="absolute bottom-4 right-4 font-mono-tactical text-[10px] uppercase tracking-widest text-slate-600 bg-white/80 px-2 py-1 border border-slate-200">
                Model · {product.model_number}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-6">
            <div className="overline mb-3 text-red-600">// {product.short_name} · MKC Catalog</div>
            <h1 className="font-display font-black uppercase text-[#0A1128] text-4xl md:text-5xl tracking-tighter leading-[0.95]" data-testid="product-name">
              {product.name}
            </h1>
            <p className="mt-4 text-blue-600 font-mono-tactical uppercase tracking-widest text-xs">{product.tagline}</p>
            <p className="mt-6 text-slate-700 text-base leading-relaxed">{product.description}</p>

            {/* Specs */}
            <div className="mt-8 border border-slate-200 bg-white">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                <span className="overline text-red-600">// Technical Specifications</span>
                <span className="font-mono-tactical text-[10px] text-slate-500">REV.A</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2">
                {product.specs.map((s, i) => (
                  <div key={i} className="border-b border-slate-200 sm:[&:nth-last-child(-n+2)]:border-b-0 px-5 py-4">
                    <div className="overline">{s.label}</div>
                    <div className="font-display font-medium text-[#0A1128] text-sm mt-1 leading-snug">{s.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Features */}
            {product.features?.length > 0 && (
              <div className="mt-8">
                <div className="overline mb-3 text-red-600">// Features</div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {product.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-slate-700 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CTAs */}
            <div className="mt-10 flex flex-wrap gap-3">
              <Button asChild data-testid="product-cta-quote" className="bg-red-600 hover:bg-red-700 text-white rounded-none uppercase font-bold tracking-wide h-12 px-6">
                <a href="#contact">Request Quote <ArrowRight className="w-4 h-4 ml-2" /></a>
              </Button>
              <Button asChild data-testid="product-cta-call" variant="outline" className="border-2 border-blue-600 bg-white text-blue-600 hover:bg-blue-50 rounded-none uppercase font-bold tracking-wide h-12 px-6">
                <a href={`tel:${SITE.phonesRaw[0]}`}><Phone className="w-4 h-4 mr-2" /> Call Now</a>
              </Button>
              <Button asChild data-testid="product-cta-email" variant="outline" className="border border-slate-300 bg-white text-[#0A1128] hover:bg-slate-50 rounded-none uppercase font-bold tracking-wide h-12 px-6">
                <a href={`mailto:${SITE.email}?subject=Inquiry: ${encodeURIComponent(product.name + " (" + product.model_number + ")")}`}><Mail className="w-4 h-4 mr-2" /> Email</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="border-t border-slate-200 bg-slate-50 py-20">
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
              <div className="flex items-end justify-between mb-10">
                <div>
                  <div className="overline mb-2 text-red-600">// Related from {product.category_name}</div>
                  <h2 className="font-display font-black uppercase text-3xl md:text-4xl text-[#0A1128]">More from this domain</h2>
                </div>
                <Link to={`/products?category=${product.category_slug}`} className="text-xs uppercase tracking-widest font-bold text-blue-600 hover:text-blue-700">
                  See all <ArrowRight className="inline w-3.5 h-3.5 ml-1" />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                {related.map((p) => (
                  <Link key={p.id} to={`/products/${p.slug}`} className="bg-white p-6 lift hover:bg-slate-50">
                    <div className="aspect-square bg-slate-50 border border-slate-200 mb-4 flex items-center justify-center">
                      <ProductImage product={p} />
                    </div>
                    <h3 className="font-display font-bold uppercase text-[#0A1128] text-base tracking-tight">{p.name}</h3>
                    <div className="font-mono-tactical text-[10px] tracking-widest text-slate-500 mt-1">{p.model_number}</div>
                    <div className="mt-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-blue-600">
                      View <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <ContactForm defaultProductSlug={product.model_number} defaultCategory={product.category_slug} />
      </main>
      <Footer />
    </div>
  );
}
